import React, { useState, useMemo, useCallback, useEffect } from "react";
import { api, formatBRL } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Plus, RefreshCw, CheckSquare, Square, Calculator, Loader2, Send,
  FileText, Trash2, ChevronDown, ChevronUp, ArrowLeft, Package, Check, X, AlertCircle,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
// Tipos de lastro (item 23c): constante ÚNICA do front, espelho dos 25 valores
// da V2 — substitui a lista local que só tinha 6 tipos e exibia o código cru.
import { BACKING_TYPE_OPTIONS, DEFAULT_BACKING_TYPE, backingTypeLabel } from "../lib/backingTypes";
// Fees da antecipação (round γ, decisão do owner 2026-07-16): cedente
// governado NUNCA envia fees (a V2 resolve pela policy — o corretivo da V2
// devolve 422 a override quando o cedente tem a Zemo como originadora apenas,
// identificador de código `direct_channel`, antigo "canal direto"); parceiro
// só envia com opt-in explícito (campo preenchido). Regra única e testada em
// lib/stockFees.js.
import { buildStockFees, stockFeeLabel, buildAnticipationBank, stockFeesError } from "../lib/stockFees";
import { maskMoneyCentsBR, maskPctBR, maskDigits, finalizeMoneyBR, parseDecimalBR, MONEY_CENTS_HINT } from "../lib/numberFormat";
import { stockItemsError, isFilledStockItem, stockItemsTotals } from "../lib/operations";
// Seleção por cedente (round δ, decisão do owner 2026-07-16): a visão do
// originador segue multi-cedente, mas a SELEÇÃO para originar é de UM
// cedente por vez (a V2 422a `mixed_assignor_anticipation` grupo misto).
// Regra única e testada em lib/stockSelection.js.
import {
  assignorKeyOf, currentAssignorOf, isItemBlocked, toggleSelection,
  selectAllOfAssignor, distinctAssignors, anticipationErrorMessage,
  pruneSelection, assignorDisplayName, SINGLE_ASSIGNOR_HINT,
} from "../lib/stockSelection";
// Detalhamento da simulação (escada δ-r1 G2): shape REAL da resposta da V2
// (`receivables` + `discount_brl`) vive em lib/stockSimulation.js, testado
// com fixture fiel à fonte.
import { simulationDetailRows } from "../lib/stockSimulation";
// ────────────────────────────────────────────────

const emptyItem = () => ({
  _key: Date.now() + Math.random(),
  assignor_id: "", payer_id: "", external_id: "", backing_type: DEFAULT_BACKING_TYPE,
  gross_face_value: "", net_face_value: "", due_date: "", nfe_key: "", pre_authorized: true,
});

// ── Views ──
const VIEW_LIST = "list";
const VIEW_REGISTER = "register";
const VIEW_SIMULATE = "simulate";
const VIEW_CONFIRM = "confirm";

export default function Stock() {
  const { user } = useAuth();
  const isCedente = user?.portal_type === "cedente";
  const [view, setView] = useState(VIEW_LIST);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  // Assignors/Payers for register form
  const [assignors, setAssignors] = useState([]);
  const [payers, setPayers] = useState([]);

  // Register
  const [newItems, setNewItems] = useState([emptyItem()]);
  const [registering, setRegistering] = useState(false);

  // Simulate
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  // Sem defaults hardcoded (antes taxa="3.5"/floating="2", enviados em TODA
  // simulação/solicitação): em branco ⇒ fees OMITIDOS e a V2 aplica a taxa
  // padrão da policy. Parceiro pode digitar uma taxa (a V2 valida a banda).
  const [taxa, setTaxa] = useState("");
  const [floating, setFloating] = useState("");
  const [showDetail, setShowDetail] = useState(false);

  // Confirm
  const [submitting, setSubmitting] = useState(false);

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchWithV2Fallback("/v2/stock", null, { status: "IN_STOCK" });
      const list = result.data?.items || (Array.isArray(result.data) ? result.data : []);
      setStockItems(list);
      // Escada δ-r1 G1: PODA a seleção a cada load — item que saiu da janela
      // (paginação limit=50 da V2, consumo concorrente) não sobrevive como
      // fantasma no Set, senão fura o single-cedente (a V2 main deployada
      // ainda não tem o guard mixed_assignor_anticipation).
      const inStock = list.filter((i) => i.status === "IN_STOCK");
      setSelected((prev) => pruneSelection(inStock, prev));
    } catch {
      setStockItems([]);
      // Fail-closed: sem lista corrente, sem seleção.
      setSelected((prev) => pruneSelection([], prev));
    }
    finally { setLoading(false); }
  }, []);

  const loadAssignorsPayers = useCallback(async () => {
    try {
      const [aRes, pRes] = await Promise.all([
        fetchWithV2Fallback("/v2/payers", null).catch(() => ({ data: [] })),
        fetchWithV2Fallback("/v2/payers", null).catch(() => ({ data: [] })),
      ]);
      // Assignors come from local cedentes (which have v2_assignor_id)
      try {
        const { data } = await api.get("/cedentes");
        setAssignors((data || []).filter(c => c.v2_assignor_id).map(c => ({ id: c.v2_assignor_id, legal_name: c.razao_social, document: c.cnpj })));
      } catch { setAssignors([]); }
      const payerList = pRes.data?.items || (Array.isArray(pRes.data) ? pRes.data : []);
      setPayers(payerList);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStock(); loadAssignorsPayers(); }, [loadStock, loadAssignorsPayers]);

  const inStockItems = useMemo(() => stockItems.filter(i => i.status === "IN_STOCK"), [stockItems]);
  const selectedItems = useMemo(() => inStockItems.filter(i => selected.has(i.id)), [inStockItems, selected]);
  // Vocabulário canônico da V2 (corte 2.0): os itens vêm de GET /v2/stock →
  // V2 `GET /v1/portal/stock` (response_model StockListResponse[StockItemSummary]),
  // que ecoa `net_future_value` ao lado do deprecado `net_face_value`. Seleção
  // por `??` (nunca `||`): canônico `0`/`"0.00"` legítimo não pode cair no
  // legado. A cadeia legada segue intacta como fallback (V2 pré-eco).
  // ATENÇÃO: só a LEITURA migra — a chave do BODY continua
  // `requested_advance_value` (StockSimulatePayload/StockRequestPayload do
  // Portal declaram o nome legado; renomear = 422 no próprio Portal).
  const totalSelected = useMemo(() => selectedItems.reduce((s, i) => s + parseFloat(i.net_future_value ?? (i.net_face_value || i.requested_advance_value || 0)), 0), [selectedItems]);

  // Cedente corrente da seleção + cedentes distintos do estoque visível.
  const currentAssignor = useMemo(() => currentAssignorOf(inStockItems, selected), [inStockItems, selected]);
  const assignorOptions = useMemo(() => distinctAssignors(inStockItems), [inStockItems]);
  const currentAssignorItems = useMemo(
    () => (currentAssignor ? inStockItems.filter(i => assignorKeyOf(i) === currentAssignor.key) : []),
    [inStockItems, currentAssignor],
  );
  // `.length > 0`: seleção fantasma (currentAssignorItems vazio) não pode
  // parecer "todos selecionados" (every de lista vazia é true).
  const allOfAssignorSelected = currentAssignor && currentAssignorItems.length > 0 && currentAssignorItems.every(i => selected.has(i.id));

  // Escada δ-r1 M2: a lista da V2 não devolve assignor_name — exibir a razão
  // social do cadastro local (assignors já carregado), nunca o UUID cru.
  const assignorLegalNames = useMemo(() => new Map(assignors.map(a => [a.id, a.legal_name])), [assignors]);
  const assignorLabel = useCallback(
    (key, name) => assignorDisplayName(key, name, assignorLegalNames),
    [assignorLegalNames],
  );

  // Marcar item de OUTRO cedente com seleção ativa é NO-OP (linha desabilitada
  // com hint); desmarcar é sempre permitido.
  const toggleItem = (item) => setSelected(prev =>
    toggleSelection(prev, item, currentAssignorOf(inStockItems, prev)?.key));
  // "Selecionar todos" é POR CEDENTE: com seleção ativa, alterna todos os itens
  // do cedente corrente; sem seleção e com 1 só cedente no estoque, seleciona
  // tudo dele; com >1 cedente, quem chama é o dropdown (pede o cedente).
  const toggleAllOfAssignor = () => {
    if (currentAssignor) {
      setSelected(allOfAssignorSelected ? new Set() : selectAllOfAssignor(inStockItems, currentAssignor.key));
    } else if (assignorOptions.length === 1) {
      setSelected(selectAllOfAssignor(inStockItems, assignorOptions[0].key));
    }
  };

  const addNewItem = () => setNewItems(p => [...p, emptyItem()]);
  const removeNewItem = (key) => setNewItems(p => p.filter(i => i._key !== key));
  const updateNewItem = (key, field, val) => setNewItems(p => p.map(i => i._key === key ? { ...i, [field]: val } : i));
  // Blur do campo de dinheiro: completa as casas ("R$ 12.000" -> "R$ 12.000,00").
  // Só escreve se mudou — completar zeros não altera o número que o
  // `parseDecimalBR` devolve no payload.
  const finalizeMoneyItem = (key, field, value) => {
    const done = finalizeMoneyBR(value);
    if (done !== value) updateNewItem(key, field, done);
  };

  const handleRegister = async () => {
    // O lote é SEQUENCIAL: um item inválido no meio deixaria os anteriores já
    // persistidos. Por isso TODOS são validados ANTES do primeiro POST — e o
    // gate é o mesmo `parseDecimalBR(v) > 0` do wizard, não truthiness (texto
    // cru inválido passava e virava NaN→null no corpo, com toast de sucesso).
    const erro = stockItemsError(newItems);
    if (erro) { toast.error(erro); return; }
    const paraRegistrar = newItems.filter(isFilledStockItem);
    setRegistering(true);
    try {
      for (const item of paraRegistrar) {
        await api.post("/v2/stock", {
          assignor_id: item.assignor_id,
          payer_id: item.payer_id,
          external_id: item.external_id,
          backing_type: item.backing_type,
          // Campos MASCARADOS ("R$ 12.000,00"): parseFloat leria 12.
          // `?? NaN` mantém o shape de antes para campo vazio.
          gross_face_value: parseDecimalBR(item.gross_face_value) ?? NaN,
          net_face_value: parseDecimalBR(item.net_face_value) ?? NaN,
          due_date: item.due_date,
          pre_authorized: item.pre_authorized,
          nfe_key: item.nfe_key || undefined,
        });
      }
      toast.success(`${paraRegistrar.length} recebivel(is) registrado(s) no estoque`);
      setNewItems([emptyItem()]);
      setView(VIEW_LIST);
      loadStock();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar no estoque");
    } finally { setRegistering(false); }
  };

  const handleSimulate = async () => {
    // Escada δ-r1 G1: payload SÓ com a interseção seleção ∩ lista corrente
    // (selectedItems) — NUNCA o Set cru, que pode carregar id fantasma de
    // outro cedente e furar o single-cedente na V2 main (sem guard).
    // requested_advance_value (totalSelected) já soma os MESMOS itens.
    const ids = selectedItems.map(i => i.id);
    if (ids.length === 0) {
      toast.error("Nenhum recebível selecionado disponível no estoque — atualize a lista e selecione novamente.");
      return;
    }
    // Override PREENCHIDO e inválido não pode virar "simula pela policy" em
    // silêncio: o usuário pediu uma taxa que não vai acontecer.
    const feesErro = stockFeesError({ isCedente, taxa, floating });
    if (feesErro) { toast.error(feesErro); return; }
    setSimulating(true);
    setSimResult(null);
    try {
      const body = {
        stock_item_ids: ids,
        requested_advance_value: totalSelected,
      };
      const simFees = buildStockFees({ isCedente, taxa, floating });
      if (simFees) body.fees = simFees;
      const { data } = await api.post("/v2/stock/simulate-anticipation", body);
      setSimResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro na simulacao";
      // Defesa em profundidade: 422 mixed_assignor_anticipation da V2 vira
      // copy clara (o front já impede seleção mista — round δ).
      const mixed = anticipationErrorMessage(detail);
      if (mixed) toast.error(mixed);
      else if (typeof detail === "string" && detail.includes("não configurada")) toast.info("V2 indisponivel neste ambiente");
      else toast.error(typeof detail === "string" ? detail : "Erro na simulacao");
    } finally { setSimulating(false); }
  };

  const handleSubmit = async () => {
    // Escada δ-r1 G1: mesma regra do simulate — ids da interseção, nunca o Set cru.
    const ids = selectedItems.map(i => i.id);
    if (ids.length === 0) {
      toast.error("Nenhum recebível selecionado disponível no estoque — atualize a lista e selecione novamente.");
      return;
    }
    const feesErro = stockFeesError({ isCedente, taxa, floating });
    if (feesErro) { toast.error(feesErro); return; }
    setSubmitting(true);
    try {
      const body = {
        stock_item_ids: ids,
        requested_advance_value: totalSelected,
        // PIX no CNPJ é o ÚNICO meio de pagamento implementado — o ramo
        // "conta bancária" mandava banco/agência/conta VAZIOS a partir de uma
        // UI sem estado (removida; decisão do owner 2026-08-03). Regra única e
        // testada POR EXECUÇÃO em lib/stockFees.js.
        bank: buildAnticipationBank(),
      };
      const reqFees = buildStockFees({ isCedente, taxa, floating });
      if (reqFees) body.fees = reqFees;
      await api.post("/v2/stock/request-anticipation", body);
      toast.success("Antecipacao solicitada com sucesso");
      setView(VIEW_LIST);
      setSelected(new Set());
      setSimResult(null);
      loadStock();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const mixed = anticipationErrorMessage(detail);
      if (mixed) toast.error(mixed);
      else toast.error(typeof detail === "string" && detail ? detail : "Erro ao solicitar antecipacao");
    } finally { setSubmitting(false); }
  };

  const goToSimulate = () => { setSimResult(null); setShowDetail(false); setView(VIEW_SIMULATE); };

  // ── Agregados da simulação, vocabulário canônico da V2 ─────────────────────
  // `simResult` vem de POST /v2/stock/simulate-anticipation → proxy cru do
  // Portal → V2 `POST /v1/stock/simulate-anticipation`
  // (response_model=StockSimulationResponse, AdditiveVocabularyOut), que ecoa
  // os 4 canônicos ao lado dos 4 deprecados. Consts derivadas para que os
  // DOIS lugares que exibem (simulação e confirmação) leiam o MESMO número.
  // Seleção por `??`: com `||` um canônico `"0.00"`/`0` cairia no legado.
  const oprGrossValue = simResult?.opr_gross_future_value ?? simResult?.opr_gross_face_value;
  const oprNetValue = simResult?.opr_net_future_value ?? simResult?.opr_net_face_value;
  const oprLiquidValue = simResult?.opr_net_present_liquid_value ?? simResult?.opr_liquid_value;
  const oprDiscountValue = simResult?.opr_present_value_discount ?? simResult?.opr_discounted_value;

  // ════════════════════════════════════════
  // VIEW: REGISTER (cadastrar recebiveis)
  // ════════════════════════════════════════
  if (view === VIEW_REGISTER) return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="stock-register-view">
      <div>
        <button onClick={() => setView(VIEW_LIST)} className="text-[13px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar para o estoque
        </button>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">Cadastrar Recebiveis</h1>
        <p className="text-sm text-zinc-500 mt-1">Registre NFs, duplicatas ou contratos no estoque V2. Voce pode antecipar depois.</p>
      </div>

      <div className="zemo-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Itens para registrar</h2>
          <Button variant="outline" size="sm" onClick={addNewItem} className="gap-1.5" data-testid="register-add-item">
            <Plus className="h-3.5 w-3.5" /> Adicionar item
          </Button>
        </div>

        {newItems.map((item, idx) => (
          <div key={item._key} className="border border-[var(--zemo-border)] rounded-lg p-4 bg-zinc-50/30" data-testid={`register-item-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <FileText className="h-4 w-4 text-[#0055FF]" /> Item #{idx + 1}
              </div>
              {newItems.length > 1 && (
                <button onClick={() => removeNewItem(item._key)} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[12px] text-zinc-500">Cedente (V2)</Label>
                <Select value={item.assignor_id} onValueChange={(v) => updateNewItem(item._key, "assignor_id", v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione cedente..." /></SelectTrigger>
                  <SelectContent>{assignors.map(a => <SelectItem key={a.id} value={a.id}>{a.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[12px] text-zinc-500">Sacado (V2)</Label>
                <Select value={item.payer_id} onValueChange={(v) => updateNewItem(item._key, "payer_id", v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione sacado..." /></SelectTrigger>
                  <SelectContent>{payers.map(p => <SelectItem key={p.id} value={p.id}>{p.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-[12px] text-zinc-500">ID externo</Label><Input value={item.external_id} onChange={e => updateNewItem(item._key, "external_id", e.target.value)} placeholder="NF-2026-001" className="h-9 text-sm" /></div>
              <div className="space-y-1">
                <Label className="text-[12px] text-zinc-500">Tipo lastro</Label>
                <Select value={item.backing_type} onValueChange={(v) => updateNewItem(item._key, "backing_type", v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{BACKING_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label htmlFor={`stock-item-gross-${idx}`} className="text-[12px] text-zinc-500">Valor bruto (R$)</Label><Input id={`stock-item-gross-${idx}`} type="text" inputMode="numeric" value={item.gross_face_value} onChange={e => updateNewItem(item._key, "gross_face_value", maskMoneyCentsBR(e.target.value, item.gross_face_value))} onBlur={e => finalizeMoneyItem(item._key, "gross_face_value", e.target.value)} placeholder="R$ 12.000,00" className="h-9 text-sm font-mono-num" aria-describedby={`stock-item-gross-hint-${idx}`} /><p id={`stock-item-gross-hint-${idx}`} className="text-[11px] text-zinc-400" data-testid={`stock-item-gross-hint-${idx}`}>{MONEY_CENTS_HINT}</p></div>
              <div className="space-y-1"><Label htmlFor={`stock-item-net-${idx}`} className="text-[12px] text-zinc-500">Valor liquido (R$)</Label><Input id={`stock-item-net-${idx}`} type="text" inputMode="numeric" value={item.net_face_value} onChange={e => updateNewItem(item._key, "net_face_value", maskMoneyCentsBR(e.target.value, item.net_face_value))} onBlur={e => finalizeMoneyItem(item._key, "net_face_value", e.target.value)} placeholder="R$ 10.000,00" className="h-9 text-sm font-mono-num" aria-describedby={`stock-item-net-hint-${idx}`} /><p id={`stock-item-net-hint-${idx}`} className="text-[11px] text-zinc-400" data-testid={`stock-item-net-hint-${idx}`}>{MONEY_CENTS_HINT}</p></div>
              <div className="space-y-1"><Label className="text-[12px] text-zinc-500">Vencimento</Label><Input type="date" value={item.due_date} onChange={e => updateNewItem(item._key, "due_date", e.target.value)} className="h-9 text-sm font-mono-num" /></div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-[12px] text-zinc-500">Chave NF-e (opcional)</Label><Input value={item.nfe_key} onChange={e => updateNewItem(item._key, "nfe_key", e.target.value)} placeholder="44 digitos" className="h-9 text-sm font-mono-num" maxLength={44} /></div>
            </div>
          </div>
        ))}

        {/* FS-160 (UX-6): TOTAL do lote antes do POST. O lote é sequencial e
            não tem tela de revisão — a primeira confirmação do valor fora do
            próprio campo acontecia DEPOIS de registrar, e com a caixa
            registradora um dígito a mais é um ×10. Soma pelo `parseDecimalBR`,
            a mesma fronteira do payload. `role="status"` e não `alert`: é
            confirmação contínua, não interrupção. */}
        {(() => {
          const totais = stockItemsTotals(newItems);
          if (totais.count === 0) return null;
          return (
            <div role="status" className="flex flex-wrap justify-end gap-x-6 gap-y-1 pt-2 text-[13px] text-zinc-600" data-testid="register-totais">
              <span>{totais.count} recebível(is)</span>
              <span>Total bruto: <strong className="font-mono-num text-zinc-800">{formatBRL(totais.gross)}</strong></span>
              <span>Total liquido: <strong className="font-mono-num text-zinc-800">{formatBRL(totais.net)}</strong></span>
              {totais.invalidos > 0 && (
                <span className="text-red-600" data-testid="register-totais-parcial">
                  {totais.invalidos} item(ns) com valor invalido fora do total
                </span>
              )}
            </div>
          );
        })()}

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="outline" onClick={() => setView(VIEW_LIST)}><X className="h-4 w-4 mr-1.5" /> Cancelar</Button>
          <Button onClick={handleRegister} disabled={registering} className="bg-[#0055FF] hover:bg-[#0044CC] text-white gap-2" data-testid="register-submit">
            <Package className="h-4 w-4" /> {registering ? "Registrando..." : `Registrar ${newItems.length} item(ns)`}
          </Button>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════
  // VIEW: SIMULATE
  // ════════════════════════════════════════
  if (view === VIEW_SIMULATE) return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="stock-simulate-view">
      <div>
        <button onClick={() => setView(VIEW_LIST)} className="text-[13px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar para o estoque
        </button>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">Simular Antecipacao</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {selectedItems.length} recebive(is) selecionado(s)
          {currentAssignor && !currentAssignor.phantom ? <> do cedente <strong>{assignorLabel(currentAssignor.key, currentAssignor.name)}</strong></> : null} — total: {formatBRL(totalSelected)}
        </p>
      </div>

      {/* Selected items summary */}
      <div className="zemo-card p-5 space-y-3">
        <h3 className="text-sm font-semibold text-zinc-700">Itens selecionados</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 text-[11px] uppercase text-zinc-500 border-b">
              <tr>
                <th className="text-left px-4 py-2">ID</th>
                <th className="text-left px-4 py-2">Cedente</th>
                <th className="text-left px-4 py-2">Sacado</th>
                <th className="text-right px-4 py-2">Valor Liquido</th>
                <th className="text-left px-4 py-2">Vencimento</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {selectedItems.map(i => (
                <tr key={i.id}><td className="px-4 py-2 font-mono-num font-semibold">{i.external_id}</td><td className="px-4 py-2 text-zinc-700">{assignorLabel(assignorKeyOf(i), i.assignor_name)}</td><td className="px-4 py-2 text-zinc-700">{i.payer_name}</td><td className="px-4 py-2 text-right font-mono-num font-semibold">{formatBRL(i.net_future_value ?? i.net_face_value)}</td><td className="px-4 py-2 font-mono-num text-zinc-500">{i.due_date}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fee controls */}
      <div className="zemo-card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-zinc-700">Parametros da simulacao</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {!isCedente && (
            <>
              <div className="space-y-1"><Label className="text-[12px] text-zinc-500">Taxa mensal (%)</Label><Input type="text" inputMode="decimal" value={taxa} onChange={e => setTaxa(maskPctBR(e.target.value, taxa))} placeholder="Policy V2" className="h-9 text-sm font-mono-num" data-testid="sim-taxa" /></div>
              {/* Pós-β da V2: floating_days SOMA dias ao prazo cobrado do
                  deságio (antes subtraía) — não é carência. */}
              <div className="space-y-1">
                <Label className="text-[12px] text-zinc-500">Dias de floating</Label>
                <Input type="text" inputMode="numeric" value={floating} onChange={e => setFloating(maskDigits(e.target.value, floating, 3))} placeholder="Policy V2" className="h-9 text-sm font-mono-num" data-testid="sim-floating" />
                <p className="text-[11px] text-zinc-400">Somam ao prazo cobrado no deságio (25 dias + 2 ⇒ cobra 27).</p>
              </div>
            </>
          )}
          <div className={`flex items-end ${isCedente ? "sm:col-span-3" : ""}`}>
            <Button onClick={handleSimulate} disabled={simulating} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white h-9 gap-2" data-testid="sim-run-btn">
              {simulating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
              {simulating ? "Calculando..." : "Simular"}
            </Button>
          </div>
        </div>
        {isCedente
          ? <p className="text-[12px] text-zinc-400">Taxas aplicadas automaticamente conforme policy do originador.</p>
          : <p className="text-[12px] text-zinc-400">Em branco, valem a taxa e o floating da policy configurada na V2. Taxa digitada precisa estar dentro da banda aprovada.</p>}
      </div>

      {/* Result */}
      {simResult && (
        <div className="zemo-card p-5 space-y-4 border-green-200" data-testid="sim-result">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-800"><Check className="h-4 w-4" /> Resultado da simulacao V2</div>
          <div className="grid sm:grid-cols-4 gap-3">
            {[["Valor bruto", oprGrossValue], ["Valor liquido (lastro)", oprNetValue], ["Valor liquido (antecipacao)", oprLiquidValue], ["Desconto total", oprDiscountValue]].map(([l, v]) => (
              <div key={l} className="p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="text-[11px] text-green-700 uppercase tracking-wide">{l}</div>
                <div className="mt-1 font-bold font-mono-num text-green-900 text-lg">{formatBRL(parseFloat(v))}</div>
              </div>
            ))}
          </div>
          {/* Escada δ-r1 M1: average_days_in_advance da V2 é média do prazo de
              CALENDÁRIO, SEM floating (main E wip: total_days += days;
              avg = total_days // len) — rotular fielmente. Os DIAS COBRADOS
              (prazo + floating) só existem POR ITEM (days_advanced, no
              detalhamento abaixo). */}
          <div className="text-[12px] text-green-700">Prazo médio dos recebíveis: <strong>{simResult.average_days_in_advance} dias corridos</strong> (sem floating)</div>

          <button onClick={() => setShowDetail(!showDetail)} className="flex items-center gap-1.5 text-sm text-[#0055FF] hover:underline" data-testid="sim-detail-toggle">
            {showDetail ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showDetail ? "Ocultar detalhamento" : "Ver detalhamento por item"}
          </button>
          {showDetail && (
            <div className="overflow-x-auto border border-[var(--zemo-border)] rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500 border-b">
                  <tr><th className="text-left px-4 py-2">ID</th><th className="text-right px-4 py-2">Bruto</th><th className="text-right px-4 py-2">A antecipar</th><th className="text-right px-4 py-2">Liquido</th><th className="text-right px-4 py-2">Desconto</th><th className="text-right px-4 py-2">Dias cobrados</th><th className="text-left px-4 py-2">Fonte</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--zemo-border)]">
                  {/* Escada δ-r1 G2: a V2 devolve `receivables` + `discount_brl`
                      (nunca `items`/`discount_value`) — shape real testado em
                      lib/stockSimulation. */}
                  {simulationDetailRows(simResult).map(r => (
                    <tr key={r.id}><td className="px-4 py-2 font-mono-num font-semibold">{r.externalId}</td><td className="px-4 py-2 text-right font-mono-num">{formatBRL(r.gross)}</td><td className="px-4 py-2 text-right font-mono-num">{formatBRL(r.advance)}</td><td className="px-4 py-2 text-right font-mono-num font-semibold text-green-800">{formatBRL(r.liquid)}</td><td className="px-4 py-2 text-right font-mono-num text-red-600">-{formatBRL(r.discount)}</td><td className="px-4 py-2 text-right">{r.daysAdvanced}d</td><td className="px-4 py-2 text-xs text-zinc-500">{r.feeSource}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={() => setView(VIEW_CONFIRM)} className="bg-[#0055FF] hover:bg-[#0044CC] text-white gap-2" data-testid="sim-proceed-btn">
              Prosseguir para solicitar <ArrowLeft className="h-4 w-4 rotate-180" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // ════════════════════════════════════════
  // VIEW: CONFIRM (solicitar antecipacao)
  // ════════════════════════════════════════
  if (view === VIEW_CONFIRM) return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="stock-confirm-view">
      <div>
        <button onClick={() => setView(VIEW_SIMULATE)} className="text-[13px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar para simulacao
        </button>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">Solicitar Antecipacao</h1>
        <p className="text-sm text-zinc-500 mt-1">Confirme os dados e envie para a API V2.</p>
      </div>

      <div className="zemo-card p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          {(isCedente ? [
            ["Recebiveis", `${selectedItems.length} itens`],
            ["Valor bruto total", formatBRL(parseFloat(oprGrossValue || 0))],
            ["Valor liquido", formatBRL(parseFloat(oprLiquidValue || 0))],
          ] : [
            ["Itens selecionados", `${selectedItems.length} recebiveis`],
            ["Valor bruto total", formatBRL(parseFloat(oprGrossValue || 0))],
            ["Valor liquido V2", formatBRL(parseFloat(oprLiquidValue || 0))],
            ["Desconto total", formatBRL(parseFloat(oprDiscountValue || 0))],
            // δ-r1 M1: campo agregado da V2 é prazo de calendário, sem floating.
            ["Prazo médio (dias corridos)", `${simResult?.average_days_in_advance || 0} dias`],
            ["Taxa", stockFeeLabel({ isCedente, taxa, floating })],
          ]).map(([k, v]) => (
            <div key={k} className={`p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/40 ${isCedente && k === "Valor liquido" ? "sm:col-span-2" : ""}`}>
              <div className="zemo-overline">{k}</div>
              <div className="mt-1 font-semibold text-zinc-900">{v}</div>
            </div>
          ))}
        </div>

        {/* Decisão do owner 2026-08-03: o modo "Conta bancária" SAIU. Era UI
            morta e enganosa — os inputs de Banco/Agência/Conta não tinham
            `value`/`onChange`, o Select só tinha `defaultValue`, e o payload
            saía com banco/agência/conta VAZIOS: o usuário escolhia um meio de
            pagamento que não existia. PIX no CNPJ é o
            único caminho real, então a tela AFIRMA o meio em vez de oferecer
            uma escolha falsa. Implementar pagamento por conta é item de
            ROADMAP — não reimplementar aqui. */}
        <div className="space-y-3">
          <div className="zemo-overline">Dados de pagamento</div>
          <div className="p-3 rounded-lg border border-[var(--zemo-border)] bg-zinc-50/30" data-testid="bank-pix">
            <div className="font-semibold text-sm">PIX no CNPJ</div>
            <div className="text-[12px] text-zinc-500 mt-0.5">Pagamento via chave PIX do cedente</div>
          </div>
        </div>

        <div className="rounded-md p-4 border border-green-200 bg-green-50 text-[13px] text-green-900" data-testid="confirm-banner">
          Ao confirmar, a solicitacao sera enviada a API V2 com status <strong>Aguardando Aprovacao</strong>. Acompanhe na aba Operacoes.
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setView(VIEW_SIMULATE)}>Voltar</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-[#0055FF] hover:bg-[#0044CC] text-white gap-2" data-testid="confirm-submit">
            <Send className="h-4 w-4" /> {submitting ? "Enviando..." : "Solicitar antecipacao"}
          </Button>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════
  // VIEW: LIST (tela principal do estoque)
  // ════════════════════════════════════════
  return (
    <div className="space-y-6" data-testid="stock-list-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Operacoes</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Estoque de Recebiveis</h1>
          <p className="mt-1 text-sm text-zinc-500">Selecione recebiveis para simular e antecipar, ou cadastre novos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadStock} disabled={loading} data-testid="stock-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button size="sm" onClick={() => { setNewItems([emptyItem()]); setView(VIEW_REGISTER); }} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="stock-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Cadastrar recebiveis
          </Button>
        </div>
      </div>

      {loading ? <Skeleton className="h-64 rounded-lg" /> : inStockItems.length === 0 ? (
        <div className="zemo-card p-12 text-center" data-testid="stock-empty">
          <Package className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum recebivel no estoque</p>
          <p className="text-xs text-zinc-400 mt-1">Cadastre recebiveis para comecar a antecipar.</p>
        </div>
      ) : (
        <>
          {/* Selection toolbar — seleção é POR CEDENTE (round δ) */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-3">
              {!currentAssignor && assignorOptions.length > 1 ? (
                // Sem seleção prévia e estoque multi-cedente: pede o cedente.
                <Select value="" onValueChange={(key) => setSelected(selectAllOfAssignor(inStockItems, key))}>
                  <SelectTrigger className="h-8 w-64 text-sm" data-testid="stock-select-all-assignor">
                    <SelectValue placeholder="Selecionar todos de um cedente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assignorOptions.map(a => (
                      <SelectItem key={a.key} value={a.key}>{assignorLabel(a.key, a.name)} ({a.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Button variant="outline" size="sm" onClick={toggleAllOfAssignor} className="gap-1.5" data-testid="stock-select-all">
                  {allOfAssignorSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                  {allOfAssignorSelected
                    ? "Desmarcar todos"
                    : currentAssignor
                      ? `Selecionar todos do cedente (${currentAssignorItems.length})`
                      : `Selecionar todos (${inStockItems.length})`}
                </Button>
              )}
              {currentAssignor && !currentAssignor.phantom && assignorOptions.length > 1 && (
                <div className="flex items-center gap-1.5 text-[12px] text-amber-700" data-testid="stock-assignor-hint">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{SINGLE_ASSIGNOR_HINT} Cedente atual: <strong>{assignorLabel(currentAssignor.key, currentAssignor.name)}</strong>.</span>
                </div>
              )}
            </div>
            {/* δ-r1 L1: contadores derivam de selectedItems (interseção com a
                lista) — nunca selected.size, que fantasmas inflariam. */}
            {selectedItems.length > 0 && (
              <div className="text-sm text-zinc-600">
                <span className="font-semibold text-zinc-900">{selectedItems.length}</span> selecionado(s) — <span className="font-mono-num font-semibold">{formatBRL(totalSelected)}</span>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="zemo-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                  <tr>
                    <th className="w-10 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={Boolean(allOfAssignorSelected) || (assignorOptions.length === 1 && selectedItems.length === inStockItems.length && inStockItems.length > 0)}
                        onChange={toggleAllOfAssignor}
                        disabled={!currentAssignor && assignorOptions.length > 1}
                        title={!currentAssignor && assignorOptions.length > 1 ? "Antecipação é por cedente — marque um item ou use “Selecionar todos de um cedente”." : undefined}
                        className="rounded border-zinc-300 disabled:opacity-40"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-semibold">ID Externo</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Tipo</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Cedente</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Sacado</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Valor Bruto</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Valor Liquido</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Vencimento</th>
                    <th className="text-center px-4 py-2.5 font-semibold">Pre-aut.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="stock-table">
                  {inStockItems.map(item => {
                    const checked = selected.has(item.id);
                    // Seleção ativa ⇒ itens de OUTROS cedentes ficam desabilitados
                    // (a antecipação é por cedente — toggleSelection é NO-OP neles).
                    const blocked = !checked && isItemBlocked(item, currentAssignor?.key);
                    return (
                      <tr
                        key={item.id}
                        onClick={() => toggleItem(item)}
                        title={blocked ? SINGLE_ASSIGNOR_HINT : undefined}
                        className={`transition-colors ${blocked ? "cursor-not-allowed opacity-40" : checked ? "cursor-pointer bg-blue-50/50" : "cursor-pointer hover:bg-zinc-50/60"}`}
                        data-testid={`stock-row-${item.id}`}
                        data-blocked={blocked || undefined}
                      >
                        <td className="px-3 py-3"><input type="checkbox" checked={checked} disabled={blocked} onChange={() => {}} className="rounded border-zinc-300 pointer-events-none disabled:opacity-40" /></td>
                        <td className="px-4 py-3 font-mono-num font-semibold text-zinc-900">{item.external_id}</td>
                        <td className="px-4 py-3 text-xs text-zinc-600">{backingTypeLabel(item.backing_type)}</td>
                        <td className="px-4 py-3 text-zinc-700">{assignorLabel(assignorKeyOf(item), item.assignor_name)}</td>
                        <td className="px-4 py-3 text-zinc-700">{item.payer_name}</td>
                        <td className="px-4 py-3 text-right font-mono-num">{formatBRL(item.gross_future_value ?? item.gross_face_value)}</td>
                        <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatBRL(item.net_future_value ?? item.net_face_value)}</td>
                        <td className="px-4 py-3 font-mono-num text-zinc-500">{item.due_date}</td>
                        <td className="px-4 py-3 text-center">{item.pre_authorized ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-zinc-300 mx-auto" />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action bar */}
          {selectedItems.length > 0 && (
            <div className="sticky bottom-4 flex items-center justify-between p-4 rounded-xl bg-white border border-[var(--zemo-border)] shadow-lg" data-testid="stock-action-bar">
              <div>
                <div className="text-sm font-semibold text-zinc-900">{selectedItems.length} recebive(is) selecionado(s)</div>
                <div className="text-[12px] text-zinc-500">Total liquido: <span className="font-mono-num font-semibold text-zinc-800">{formatBRL(totalSelected)}</span></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setSelected(new Set())} size="sm">Limpar</Button>
                <Button onClick={goToSimulate} className="bg-[#0055FF] hover:bg-[#0044CC] text-white gap-2" data-testid="stock-simulate-btn">
                  <Calculator className="h-4 w-4" /> Simular antecipacao
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
